<!-- One transloco scope wrapping everything: the shared `#matAutocomplete` below has to sit in the
     same view as the appearance branches that reference it, or the template ref will not resolve. -->
<ng-container *transloco="let t">

<!-- Bar search -->
<ng-container *ngIf="appearance === 'bar'">
    <button
        mat-icon-button
        *ngIf="!opened"
        [matTooltip]="t('Search...')"
        [attr.aria-label]="t('Search...')"
        (click)="open()">
        <mat-icon [svgIcon]="'heroicons_outline:magnifying-glass'"></mat-icon>
    </button>
    <div
        class="absolute inset-0 flex items-center shrink-0 z-99 bg-card"
        *ngIf="opened"
        @slideInTop
        @slideOutTop>
        <mat-icon
            class="absolute ml-6 sm:ml-8"
            [svgIcon]="'heroicons_outline:magnifying-glass'"></mat-icon>
        <input
            class="w-full h-full px-16 sm:px-18"
            [formControl]="searchControl"
            [matAutocomplete]="matAutocomplete"
            [placeholder]="t('Search...')"
            [attr.aria-label]="t('Search...')"
            (keydown)="onKeydown($event)"
            #barSearchInput>
        <button
            class="absolute top-1/2 right-5 sm:right-7 shrink-0 w-10 h-10 -mt-5"
            mat-icon-button
            [matTooltip]="t('Close')"
            [attr.aria-label]="t('Close')"
            (click)="close()">
            <mat-icon [svgIcon]="'heroicons_outline:x-mark'"></mat-icon>
        </button>
    </div>
</ng-container>

<!-- Basic search: controlled-width form field (not full-bleed), matching the
     system's standard search field style (fuse-mat-dense fuse-mat-rounded), for
     docking global search directly in a toolbar instead of expanding over it. -->
<ng-container *ngIf="appearance === 'basic'">
    <div class="w-40 sm:w-56 md:w-72">
        <mat-form-field
            class="w-full fuse-mat-dense fuse-mat-rounded sai-global-search"
            [subscriptSizing]="'dynamic'">
            <mat-icon
                matPrefix
                [svgIcon]="'heroicons_outline:magnifying-glass'"></mat-icon>
            <input
                matInput
                [formControl]="searchControl"
                [matAutocomplete]="matAutocomplete"
                [placeholder]="t('Search...')"
                [attr.aria-label]="t('Search...')"
                (keydown)="onKeydown($event)">
            <mat-progress-spinner
                matSuffix
                *ngIf="loading"
                [diameter]="16"
                [strokeWidth]="2"
                [mode]="'indeterminate'"></mat-progress-spinner>
            <button
                matSuffix
                mat-icon-button
                type="button"
                class="sai-global-search__clear"
                *ngIf="!loading && searchControl.value"
                [matTooltip]="t('Clear search')"
                [attr.aria-label]="t('Clear search')"
                (click)="clear()">
                <mat-icon [svgIcon]="'heroicons_outline:x-mark'"></mat-icon>
            </button>
        </mat-form-field>
    </div>
</ng-container>

<!-- Shared autocomplete panel: loading / empty / grouped results.
     The options MUST be lexically inside <mat-autocomplete> — MatAutocomplete decides whether to
     show the panel from `@ContentChildren(MatOption).length`, and a content query does not see
     options stamped in from an <ng-template> declared elsewhere (e.g. via *ngTemplateOutlet).
     Projecting them that way leaves the panel permanently `mat-mdc-autocomplete-hidden`: the rows
     render into the DOM but the panel never becomes visible. -->
<mat-autocomplete
    [class]="appearance === 'bar' ? 'max-h-128 sai-global-search-panel' : 'max-h-128 mt-2 sai-global-search-panel'"
    [autoSelectActiveOption]="true"
    [disableRipple]="true"
    (optionSelected)="navigateToResult($event.option.value)"
    #matAutocomplete="matAutocomplete">
    <mat-option
        class="sai-global-search-panel__status"
        *ngIf="loading">
        <mat-progress-spinner
            [diameter]="14"
            [strokeWidth]="2"
            [mode]="'indeterminate'"></mat-progress-spinner>
        <span>{{ t('Searching…') }}</span>
    </mat-option>
    <mat-option
        class="sai-global-search-panel__status"
        *ngIf="!loading && resultGroups && !resultGroups.length">
        {{ t('No results found!') }}
    </mat-option>
    <ng-container *ngIf="!loading">
        <ng-container *ngFor="let group of resultGroups; trackBy: trackByGroup">
            <!-- The bare <span> lands in MatOptgroup's catch-all <ng-content>, which sits inside
                 the label; the <mat-option>s match its `select="mat-option"` slot. That is the
                 only way to get markup into an optgroup label ([label] takes a string). -->
            <mat-optgroup>
                <span class="sai-global-search-panel__group">
                    <mat-icon
                        class="sai-global-search-panel__group-icon"
                        [svgIcon]="group.icon"></mat-icon>
                    <span class="sai-global-search-panel__group-label">{{ t(group.labelKey) }}</span>
                    <span class="sai-global-search-panel__group-count">{{ group.results.length }}</span>
                </span>
                <mat-option
                    *ngFor="let result of group.results; trackBy: trackByResult"
                    [value]="result">
                    <span class="sai-global-search-panel__row">
                        <span class="truncate sai-global-search-panel__name">{{ result.name }}</span>
                        <span
                            class="truncate sai-global-search-panel__trail"
                            *ngIf="trailText(result) as trail">
                            {{ trail }}
                        </span>
                    </span>
                </mat-option>
            </mat-optgroup>
        </ng-container>
    </ng-container>
</mat-autocomplete>

</ng-container>
